import { useEffect, useId, useRef, useState } from "react"
import { Users } from "lucide-react"
import type { GalleryTeam } from "../../hooks/useCccdTeamGallery"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { ROLE_LABEL, initials, memberCountLabel, shortName } from "./teamUtils"

const VISIBLE = 4

/** Avatar row (inside the scrim) + 'Integrantes' button + rising roster panel.
 * Sibling of the card link, above it in z-order — no nested interactive content. */
export function TeamRoster({ team }: { team: GalleryTeam }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const { members } = team
  const shown = members.slice(0, VISIBLE)
  const extra = members.length - shown.length

  useEffect(() => {
    if (!open) return
    const onDoc = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("pointerdown", onDoc)
    return () => document.removeEventListener("pointerdown", onDoc)
  }, [open])

  if (members.length === 0) return null

  const hidden =
    "invisible translate-y-2 opacity-0 [@media(hover:hover)]:group-hover:visible [@media(hover:hover)]:group-hover:translate-y-0 [@media(hover:hover)]:group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100"

  return (
    <div
      ref={rootRef}
      className="pointer-events-none absolute inset-x-0 bottom-0 z-20"
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          setOpen(false)
          buttonRef.current?.focus()
        }
      }}
      onBlur={(e) => {
        if (open && !e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false)
      }}
    >
      <div
        id={panelId}
        role="region"
        aria-label={`Integrantes del equipo ${team.name}`}
        className={`pointer-events-auto absolute inset-x-2 bottom-14 max-h-[70%] overflow-y-auto rounded-lg border border-border bg-card p-2 text-card-foreground transition-[opacity,transform] duration-[var(--dur-base)] ease-[var(--ease-out-expo)] motion-reduce:transition-none motion-reduce:transform-none ${
          open ? "visible translate-y-0 opacity-100" : hidden
        }`}
      >
        <ul className="space-y-1">
          {members.map((m) => (
            <li key={m.id} className="flex items-center gap-2 text-sm">
              <Avatar size="sm">
                {m.photoUrl ? <AvatarImage src={m.photoUrl} alt="" /> : null}
                <AvatarFallback>{initials(m.firstName, m.lastName)}</AvatarFallback>
              </Avatar>
              <span className="min-w-0 truncate font-medium" title={shortName(m)}>
                {shortName(m)}
              </span>
              {ROLE_LABEL[m.role] && <span className="shrink-0 text-muted-foreground">· {ROLE_LABEL[m.role]}</span>}
            </li>
          ))}
        </ul>
      </div>

      <div className="pointer-events-auto flex items-center justify-between gap-2 px-3 pb-3 pt-1">
        <div className="flex items-center -space-x-2" aria-hidden="true">
          {shown.map((m) => (
            <Avatar key={m.id} className="size-8 ring-2 ring-black/60">
              {m.photoUrl ? <AvatarImage src={m.photoUrl} alt="" /> : null}
              <AvatarFallback className="text-xs">{initials(m.firstName, m.lastName)}</AvatarFallback>
            </Avatar>
          ))}
          {extra > 0 && (
            <span className="relative flex size-8 items-center justify-center rounded-full bg-white/20 text-xs font-semibold tabular-nums text-white ring-2 ring-black/60">
              +{extra}
            </span>
          )}
        </div>
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex min-h-8 items-center gap-1.5 rounded-md bg-white/15 px-2.5 text-sm font-semibold text-white outline-none transition-colors hover:bg-white/25 focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none [@media(pointer:coarse)]:min-h-11"
        >
          <Users className="h-4 w-4" aria-hidden="true" />
          Integrantes
          <span className="sr-only">: {memberCountLabel(members.length)}</span>
        </button>
      </div>
    </div>
  )
}
